import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { usePublishedTermsQuery } from '@/features/auth/public-terms/hooks/use-published-terms';
import { TERM_SLUG_TO_TYPE_ID, isTermSlug } from '@/features/auth/public-terms/types';

// FEATURE_041 — public (pre-login) standalone viewer for PUBLISHED legal terms, reached from
// the login/signup footer links. `?type=service|policy|privacy|sensitive` shows that single
// term; no/unknown type shows all. Ports prototype/pages/terms.html.
export const AdminTermsPage = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // `typeId` — used by the dynamic login footer (data-driven links carry the id directly).
  // `type` — slug used by the static signup footer. typeId takes precedence when both present.
  const typeIdParam = params.get('typeId');
  const slug = params.get('type');

  const { data: terms, isLoading, isError } = usePublishedTermsQuery();

  const selected = useMemo(() => {
    if (!terms) return [];
    if (typeIdParam !== null) {
      const id = Number(typeIdParam);
      return Number.isFinite(id) ? terms.filter((term) => term.typeId === id) : [];
    }
    if (isTermSlug(slug)) {
      return terms.filter((term) => term.typeId === TERM_SLUG_TO_TYPE_ID[slug]);
    }
    return terms;
  }, [terms, typeIdParam, slug]);

  // A specific term was requested (by id or slug) → title it; otherwise show the generic heading.
  const isSpecific = typeIdParam !== null || isTermSlug(slug);
  const heading = isSpecific && selected.length === 1 ? selected[0].name : t('termsPage.title');

  // Return to wherever the user came from, not a hardcoded screen:
  //  - Same-tab navigation → pop history back to the caller (signup/login).
  //  - New tab (footer / [보기] links open with target="_blank") → no history to
  //    pop, so close the tab to hand focus back to the opener. Browsers allow
  //    window.close() when the tab has a single history entry.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else window.close();
  };

  const ready = !isLoading && !isError;

  // Effective date arrives ISO (YYYY-MM-DD); the design shows dotted YYYY.MM.DD
  // (same convention as the admin Terms-Management version list).
  const formatEffectiveDate = (value: string) => value.replace(/-/g, '.');

  return (
    <div className="terms-page">
      <div className="terms-page__bar">
        <button type="button" className="terms-page__back" onClick={goBack}>
          ← {t('termsPage.back')}
        </button>
        <h1>{heading}</h1>
      </div>

      {isLoading && <p className="terms-page__note">{t('termsPage.loading')}</p>}
      {isError && (
        <p className="terms-page__note" role="alert">
          {t('termsPage.loadError')}
        </p>
      )}
      {ready && selected.length === 0 && (
        <p className="terms-page__note">{t('termsPage.notFound')}</p>
      )}

      {ready &&
        selected.map((term) => (
          <section key={term.termVersionId} className="terms-page__section">
            <h2>
              {term.name}
              <span
                className={
                  term.required
                    ? 'terms-page__tag terms-page__tag--req'
                    : 'terms-page__tag terms-page__tag--opt'
                }
              >
                {term.required ? t('termsPage.required') : t('termsPage.optional')}
              </span>
            </h2>
            {term.version && term.effectiveDate && (
              <p className="terms-page__meta">
                {t('termsPage.meta', {
                  version: term.version,
                  date: formatEffectiveDate(term.effectiveDate),
                })}
              </p>
            )}
            <div
              className="terms-page__body terms-detail-body"
              // BE owns sanitization of stored HTML; FE renders verbatim (matches SignupTermsConsent).
              dangerouslySetInnerHTML={{ __html: term.content }}
            />
          </section>
        ))}
    </div>
  );
};

export default AdminTermsPage;
